<template>
  <v-container>
    <back-button></back-button>
    <page-heading>{{ pageTitle }}</page-heading>

    <request-state :request-promise="userPromise" :always-show="!userId" :retry-action="getUser">
      <div class="d-flex justify-end mb-2" v-if="canDeleteUser">
        <v-btn
            class="ml-2 red--text"
            @click="showDeleteModal = true"
            :loading="isDeleting"
            :title="trans('general.delete')"
        ><v-icon>mdi-delete</v-icon></v-btn>
      </div>

      <v-form>
        <v-card>
          <v-card-title>{{ trans('user.settingsGeneral') }}</v-card-title>
          <v-card-text>
            <form-input
                :error-messages="validationErrors['firstname']"
                :label="trans('user.firstname')"
                v-model="userSaveRequest.firstname"
            ></form-input>
            <form-input
                :error-messages="validationErrors['surname']"
                :label="trans('user.surname')"
                v-model="userSaveRequest.surname"
            ></form-input>
            <form-input
                :error-messages="validationErrors['email']"
                :label="trans('user.email')"
                v-model="userSaveRequest.email"
            ></form-input>
            <form-input
                :error-messages="validationErrors['role']"
                :label="trans('user.role')"
                v-model="userSaveRequest.role"
                :items="userRoleOptions"
                :cols="4"
            ></form-input>
            <form-input
                :error-messages="validationErrors['mainUserId']"
                v-if="hasAnyPermission('USER_CAN_SELECT_MAIN_USER') && !['ADMIN', 'MANAGER'].includes(userSaveRequest.role)"
                :label="trans('user.mainUser')"
            >
              <template slot="input">
                <ajax-select
                    resource-url="users/manager-users"
                    item-text="fullName"
                    :value="userSaveRequest.mainUserId"
                    @update:value="userSaveRequest.mainUserId = $event"
                    :placeholder="$t('general.pleaseSelect')"
                    dense
                >
                </ajax-select>
              </template>
            </form-input>
            <form-input
                :error-messages="validationErrors['password']"
                :label="trans('user.password')"
                v-model="userSaveRequest.password"
                :help-text="userId > 0 ? trans('user.passwordHelp') : null"
                type="password"
                autocomplete="new-password"
            ></form-input>
            <form-input
                :error-messages="validationErrors['passwordConfirm']"
                :label="trans('user.passwordConfirm')"
                v-model="userSaveRequest.passwordConfirm"
                type="password"
                autocomplete="new-password"
            ></form-input>
            <form-input>
              <template v-slot:input>
                <global-validation-errors :value="validationErrors"/>
              </template>
              <template v-slot:help-text>
              </template>
            </form-input>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <save-button
                @click.native="saveUser"
                :loading="userSaveLoading"
            ></save-button>
          </v-card-actions>
        </v-card>
      </v-form>
    </request-state>

    <confirm-deletion-modal
      :show-modal.sync="showDeleteModal"
      @confirm="deleteUser"
    >
      {{ trans('user.confirmDelete') }}
    </confirm-deletion-modal>
  </v-container>
</template>

<script>
import {LocaleUtils} from '../utils/locale-utils';
import {RouterUtils} from '../utils/router-utils';
import {DateUtils} from '../utils/date-utils';
import {MainUtils} from '../utils/main-utils';
import FormInput from '../components/FormInput';
import BackButton from '../components/BackButton';
import PageHeading from '../components/PageHeading';
import {UserApi} from '../api/user-api';
import SaveButton from '../components/SaveButton';
import {UserRole} from '../lib/user-roles';
import {UserUtils} from '../utils/user-utils';
import AjaxSelect from '../components/AjaxSelect';
import ConfirmDeletionModal from '../components/ConfirmDeletionModal';
import GlobalValidationErrors from '@/components/GlobalValidationErrors';
import {mapGetters} from 'vuex';
import RequestState from '@/components/RequestState';

export default {
  name: 'UserPage',
  mixins: [
    LocaleUtils,
    RouterUtils,
    DateUtils,
    UserUtils,
    MainUtils
  ],
  components: {
    RequestState,
    GlobalValidationErrors, ConfirmDeletionModal, AjaxSelect, SaveButton, PageHeading, BackButton, FormInput},
  props: {
    userId: null
  },
  data () {
    return {
      /** @type {UserDto} */
      user: {},
      /** @type {UserSaveRequest} */
      userSaveRequest: {
        firstname: null,
        surname: null,
        email: null,
        role: null,
        mainUserId: null
      },
      validationErrors: {},
      userPromise: null,
      userSaveLoading: false,
      isDeleting: false,
      showDeleteModal: false
    }
  },
  computed: {
    ...mapGetters({
      auth: 'auth'
    }),
    userRoleOptions () {
      let userRoleOptions = [];

      Object.keys(UserRole).forEach(/** @type {UserRoleDto} */ userRole => {
        let hasPermissionToSelectRole = false;

        switch (userRole) {
          case 'ADMIN': hasPermissionToSelectRole = this.hasAnyPermission('USER_ROLE_SET_ADMIN'); break;
          case 'MANAGER': hasPermissionToSelectRole = this.hasAnyPermission('USER_ROLE_SET_MANAGER') || this.userIsAuthUser; break;
          case 'CARETAKER': hasPermissionToSelectRole = this.hasAnyPermission('USER_ROLE_SET_CARETAKER'); break;
          case 'SURVEY_REVIEWER': hasPermissionToSelectRole = this.hasAnyPermission('USER_ROLE_SET_SURVEY_REVIEWER'); break;
        }

        if (hasPermissionToSelectRole) {
          userRoleOptions.push({
            value: userRole,
            text: this.getUserRoleName(userRole)
          });
        }
      })

      return userRoleOptions;
    },
    pageTitle () {
      if (this.user && this.user.fullName) {
        return this.user.fullName;
      } else if (this.userId) {
        return this.transSingular('user.userManagement');
      } else {
        return this.trans('user.create');
      }
    },
    canDeleteUser () {
      return this.hasAnyPermission('USER_EDIT') && !this.userIsAuthUser;
    },
    userIsAuthUser () {
      return this.user && this.user.id && this.user.id === this.auth.user.id;
    }
  },
  mounted () {
    this.userSaveRequest.role = this.userRoleOptions.length === 1
      ? this.userRoleOptions[0].value
      : null
    ;

    if (this.userId) {
      this.getUser();
    }
    // this.getUserSettings(); // TODO maybe later user settings of non-current user may be edited by admin/manager
  },
  methods: {
    getUser () {
      if (this.userId) {
        this.userPromise = UserApi.getById(this.userId);
        this.userPromise.then((response) => {
          this.user = response.body;
          this.userSaveRequest.firstname = this.user.firstname;
          this.userSaveRequest.surname = this.user.surname;
          this.userSaveRequest.email = this.user.email;
          this.userSaveRequest.role = this.user.role;
          this.userSaveRequest.mainUserId = this.user.mainUserId;
        })
      }
    },
    saveUser () {
      this.userSaveLoading = true;
      if (this.userId) {
        UserApi.updateUser(this.userId, this.userSaveRequest)
        .then(this.onSaveUserSuccess)
        .catch((response) => this.validationErrors = this.getValidationErrors(response))
        .finally(this.onSaveUserComplete);
      } else {
        UserApi.createUser(this.userSaveRequest)
        .then(this.onSaveUserSuccess)
        .catch((response) => this.validationErrors = this.getValidationErrors(response))
        .finally(this.onSaveUserComplete);
      }
    },
    onSaveUserSuccess () {
      this.validationErrors = {};
      this.showSavedSuccessfullyNotification();
      this.replaceToRoute('Users');
    },
    onSaveUserComplete () {
      this.userSaveLoading = false;
    },
    deleteUser () {
      this.isDeleting = true;
      UserApi.delete(this.userId)
      .then(response => {
        this.replaceToRoute('Users');
        this.showDeletedSuccessfullyNotification();
      })
      .catch(this.handleErrorResponse)
      .finally(() => {
        this.isDeleting = false;
      })
    }
    // getUserSettings () {
    //   this.userPromise = UserApi.getUserSettings();
    //   this.userPromise.then((response) => {
    //     this.user = response.body;
    //   })
    // },
    // saveUserSettings () {
    //   this.userSaveLoading = true;
    //   UserApi.saveUserSettings(this.user)
    //   .then(response => {
    //     // TODO success
    //   })
    //   .finally(() => this.userSaveLoading = false);
    // }
  }
}
</script>

